import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, FolderOpen, Users, Calendar, Star, ArrowRight, Grid, BookOpen } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useData } from '../contexts/DataContext';
import { formatDate } from '../utils/filters';

const ProjectsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name-asc');
  const { getAllProjects, getAllComponents, isLoading, error } = useData();
  
  const projects = getAllProjects();
  const allComponents = getAllComponents();

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    if (!projects) return [];
    
    return projects
      .filter(project => 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      .sort((a, b) => {
        const [field, direction] = sortOption.split('-');
        
        let comparison = 0;
        
        switch (field) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'lastUpdated':
            comparison = new Date(a.lastUpdated || 0).getTime() - new Date(b.lastUpdated || 0).getTime();
            break;
          case 'componentsCount':
            // Use actual components length instead of componentsCount from API
            comparison = (a.components?.length || 0) - (b.components?.length || 0);
            break;
          default:
            comparison = 0;
        }
        
        return direction === 'desc' ? -comparison : comparison;
      });
  }, [projects, searchQuery, sortOption]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Error Loading Projects</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }



  // Calculate total components from actual components array
  const totalComponents = allComponents.length;

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold gradient-text mb-2">
          PatternLab Projects
        </h1>
        <p className="text-slate-600 dark:text-slate-300 text-lg">
          Explore and manage your PatternLab projects
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card card-hover liquid-glass text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {projects?.length || 0}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total Projects
          </div>
        </div>
        <div className="card card-hover liquid-glass text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {totalComponents}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total Components
          </div>
        </div>
        <div className="card card-hover liquid-glass text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {projects?.filter(p => p.lastUpdated && new Date(p.lastUpdated) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length || 0}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Recently Updated
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              Projects ({filteredAndSortedProjects.length})
            </h2>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-glass pl-10 pr-4 w-64"
              />
            </div>

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="input-glass"
            >
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="lastUpdated-desc">Recently Updated</option>
              <option value="lastUpdated-asc">Oldest First</option>
              <option value="componentsCount-desc">Most Components</option>
              <option value="componentsCount-asc">Least Components</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAndSortedProjects.map((project) => (
          <Link
            key={project.id}
            to={`/projects/${project.id}`}
            className="card card-hover liquid-glass group cursor-pointer p-6"
          >
            {/* Project Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                  {project.name}
                </h3>
                {project.description && (
                  <p className="text-slate-600 dark:text-slate-300 text-sm line-clamp-2 mb-3">
                    {project.description}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {project.status && (
                  <span className={`badge ${project.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                    {project.status}
                  </span>
                )}
              </div>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 glass-morphism rounded-xl">
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {project.components?.length || 0}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Components
                </div>
              </div>
              <div className="text-center p-3 glass-morphism rounded-xl">
                <div className="text-xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {project.version || '1.0'}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Version
                </div>
              </div>
            </div>

            {/* Project Meta */}
            <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400 mb-4">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Updated {formatDate(project.lastUpdated)}</span>
              </div>
              {project.owner && project.owner.length > 0 && (
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{project.owner.length} owner{project.owner.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
              <span className="text-sm font-medium mr-2">View Project</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>

      {filteredAndSortedProjects.length === 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            No projects found
          </h3>
          <p className="text-slate-600 dark:text-slate-300">
            Try adjusting your search terms
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
